import { Loader } from "rsuite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import PurchasingConditionDownloadModal from "../../../components/purchasing-condition";
import VendorQuoteAssessmentHeader from "./components/VendorQuoteAssessmentHeader";
import VendorQuoteAssessmentContent from "./components/VendorQuoteAssessmentContent";
import { downloadDistrictComparisonExcel } from "./utils/exportDistrictComparisonExcel";
import {
  AllFiltersState,
  INITIAL_ALL_FILTERS,
  NavItem,
  QuoteSummaryFiltersState,
  QuoteSummaryMode,
  QuoteValue,
  SkuFiltersState,
  areSameSelections,
  expandContractTypes,
  getContractTypeValues,
  getInitialNavItem,
  getMonthValues,
  removeDateField,
} from "./types";

import {
  NegotiationFilterRes,
  NegotiationSelectedFilters,
} from "../../../services/interfaces/IVendorQuoteAssessment";
import { COPPER_WIRE, getDefaultCommodity } from "../../../utils/helpers/commodityOrder";
import {
  useNegotiationFilters,
  useVendorQuoteRenegotiateData,
  useQuoteDetailsData,
  useSkuRatioData,
  useDistrictComparisonData,
  useSkuQuoteDetailsData,
  getSkuQuoteDetailsData
} from "../../../react-query/hooks/HVendorQuoteAssessment";
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";
import {
  convertDateToMonthYear,
  formatISOToLong,
} from "../../../utils/helpers/mapModulesToIcon";
import { trackTabClick } from "../../../utils/helpers/trackingEvent";
import { useAppSelector } from "../../../services/redux/hooks/hooks";

const VendorQuoteAssessment = () => {
  const width = useResponsiveWidth();
  const location = useLocation();
  const hiddenParam = location.state?.hiddenParam;
  const userDetailsRedux = useAppSelector((state) => state.user);
  const [district, setDistrict] = useState<string[] | null>([]);
  const [vendor, setVendor] = useState<string[] | null>([]);
  const [skuToggle, setSkuToggle] = useState(false);
  const [skuQuoteDetailsToggle, setSkuQuoteDetailsToggle] =
    useState<QuoteSummaryMode>("supplier");
  const [quoteToggle, setQuoteToggle] = useState(false);
  const [navItem, setNavItem] = useState<NavItem>(getInitialNavItem(hiddenParam));
  const [quotes, setQuotes] = useState<QuoteValue>("11");
  const [monthYear, setMonthYear] = useState("");
  const [contractType, setContractType] = useState<string[] | null>([]);
  const [benchmark, setBenchmark] = useState("");
  const [openDownloadModal, setOpenDownloadModal] = useState(false);

  const currentSelection = useRef<string[] | null>(null);
  const changedFilter = useRef<string | null>(null);

  const [allFilters, setAllFilters] = useState<AllFiltersState>(INITIAL_ALL_FILTERS);

  const [filters, setFilters] = useState<NegotiationSelectedFilters>({
    // Seed the default commodity so the very first
    // getAllFiltersUpcomingMonthlyNegotiations call already carries
    // commodity (+ module=wire). The promote effect below validates this
    // against the commodity list returned by the API and corrects it for any
    // deployment whose list does not include Copper Wire.
    commodity: COPPER_WIRE,
    districts: null,
    vendor: null,
    contractTypes: null,
    contractValidity: null,
    benchmark: null,
  });

  const [skuVendor, setSkuVendor] = useState<string[]>([]);
  const [skuDistrict, setSkuDistrict] = useState<string[]>([]);
  const [skuQuoteDetailsVendor, setSkuQuoteDetailsVendor] = useState<string[]>(
    [],
  );
  const [skuQuoteDetailsDistrict, setSkuQuoteDetailsDistrict] = useState<
    string[]
  >([]);
  const [skuQuoteDetailsDate, setSkuQuoteDetailsDate] = useState<string[]>([]);

  const [quoteSummaryContractType, setQuoteSummaryContractType] = useState<
    string[] | null
  >([]);
  const [quoteSummaryBenchmark, setQuoteSummaryBenchmark] = useState("");

  const [skuFilters, setSkuFilters] = useState<SkuFiltersState>({
    commodity: null,
    districts: [],
    vendor: [],
    contractTypes: [],
    contractValidity: [],
    benchmark: "",
  });

  const [skuQuoteDetailsFilters, setSkuQuoteDetailsFilter] =
    useState<QuoteSummaryFiltersState>({
      commodity: null,
      districts: [],
      vendor: [],
      date: [],
      contractTypes: [],
      contractValidity: [],
      benchmark: "",
    });

  const [isQuoteSummaryReady, setIsQuoteSummaryReady] = useState(false);

  const firstDistrict = allFilters.districts?.[0]?.district ?? "";
  const firstVendor = allFilters.vendor?.[0]?.vendor ?? "";
  const firstMonth = allFilters.month_year?.[0]?.month ?? "";

  const quoteSummarySelectedMonth =
    skuQuoteDetailsFilters.date.length > 0
      ? skuQuoteDetailsFilters.date[0]
      : null;

  const negotiationFilterDistricts =
    navItem === "sKURatioAnalysis"
      ? skuFilters.districts.length > 0
        ? skuFilters.districts
        : null
      : navItem === "quoteSummary"
        ? skuQuoteDetailsFilters.districts.length > 0
          ? skuQuoteDetailsFilters.districts
          : null
        : filters.districts;

  const negotiationFilterVendors =
    navItem === "sKURatioAnalysis"
      ? skuFilters.vendor.length > 0
        ? skuFilters.vendor
        : null
      : navItem === "quoteSummary"
        ? skuQuoteDetailsFilters.vendor.length > 0
          ? skuQuoteDetailsFilters.vendor
          : null
        : filters.vendor;

  const negotiationContractTypes =
    filters.commodity === "Copper Wire"
      ? navItem === "sKURatioAnalysis"
        ? skuFilters.contractTypes.length > 0
          ? skuFilters.contractTypes
          : null
        : navItem === "quoteSummary"
          ? skuQuoteDetailsFilters.contractTypes.length > 0
            ? skuQuoteDetailsFilters.contractTypes
            : null
          : navItem === "districtComparison"
            ? filters.contractTypes
            : null
      : null;

  const negotiationBenchmark =
    navItem === "quoteSummary"
      ? skuQuoteDetailsFilters.benchmark
        ? [skuQuoteDetailsFilters.benchmark]
        : null
      : benchmark
        ? [benchmark]
        : null;

  const {
    fetchFilters,
    isLoading,
    isError: isFiltersError,
  } = useNegotiationFilters(
    localStorage.getItem("userEmail"),
    filters.commodity,
    negotiationFilterDistricts,
    negotiationFilterVendors,
    // Quote Details tab (navItem "quoteSummary") and Quote Summary tab
    // (navItem "quoteDetails") must NOT send month_year to the filters
    // (options) endpoint. The selected month is applied to the data call
    // separately, so keeping it out here prevents the default auto-selected
    // month from leaking into getAllFiltersUpcomingMonthlyNegotiations.
    navItem !== "quoteDetails" && navItem !== "quoteSummary"
      ? monthYear
      : null,
    negotiationContractTypes,
      null,
    negotiationBenchmark,
    (navItem === 'quoteSummary' && filters.commodity !== 'Copper Wire') && quotes ? [quotes] : null,
    navItem,
  );

  const fallbackDistricts =
    fetchFilters.districts?.length > 0
      ? fetchFilters.districts.map((item) => item.district)
      : null;

  const fallbackVendors =
    fetchFilters.vendor?.length > 0
      ? fetchFilters.vendor.map((item) => item.vendor)
      : null;

  const monthOfFilters = (fetchFilters.date ?? []).reduce<
    Array<{ month: string }>
  >((acc, item) => {
    const monthValue = item?.month_year
      ? convertDateToMonthYear(item.month_year)
      : "";

    if (monthValue && !acc.some((entry) => entry.month === monthValue)) {
      acc.push({ month: monthValue });
    }

    return acc;
  }, []);

  const isQuoteSummaryMonthOf =
    filters.commodity === "Copper Wire" &&
    skuQuoteDetailsFilters.contractTypes.some(
      (value) => value === "Firm" || value === "Float" || value === "Firm/Float" || value === "Firm,Float",
    );

  const isSkuMonthOf =
    filters.commodity === "Copper Wire" &&
    (contractType ?? []).some(
      (value) => value === "Firm" || value === "Float" || value === "Firm/Float" || value === "Firm,Float",
    );

  const firstMonthOf = monthOfFilters[0]?.month ?? "";

  const fallbackMonths =
    fetchFilters.month_year?.length > 0
      ? fetchFilters.month_year.map((item) => item.month)
      : null;

  const { vendorData, isLoading: renegotiateDataLoading } =
    useVendorQuoteRenegotiateData(
      filters.commodity,
      filters.districts && filters.districts.length > 0
        ? filters.districts
        : fallbackDistricts,
      filters.vendor,
      filters.commodity === "Copper Wire" && benchmark ? [benchmark] : null,
      navItem,
    );

  // "Firm/Float" is a combined display value used by other tabs and is never a valid
  // District Comparison quote type. Sanitize the shared contractType before it reaches the
  // getDotchartVendorData query so it never fires with "Firm/Float" (and doesn't double-call).
  const districtComparisonContractType = useMemo(() => {
    if (navItem !== "districtComparison" || filters.commodity !== "Copper Wire") {
      return contractType;
    }
    const availableQuoteTypes =
      fetchFilters.contract_type?.map((item) => item.contract_type) ?? [];
    const currentType = contractType?.[0] ?? "";
    const isValid =
      currentType !== "" &&
      currentType !== "Firm/Float" &&
      (availableQuoteTypes.length === 0 || availableQuoteTypes.includes(currentType));
    if (isValid) {
      return contractType;
    }
    if (availableQuoteTypes.length > 0) {
      return availableQuoteTypes.includes("Firm LTC")
        ? ["Firm LTC"]
        : [availableQuoteTypes[0]];
    }
    return ["Firm LTC"];
  }, [navItem, filters.commodity, contractType, fetchFilters]);

  const { districtComparisionData, isLoading: districtComparisionLoading } =
    useDistrictComparisonData(
      filters.commodity,
      monthYear,
      quotes,
      districtComparisonContractType,
      benchmark,
      navItem,
    );

  const spotContractTypes = new Set([
    "spot",
    "Spot",
    "SPOT",
    "spot LTC",
    "Spot LTC",
    "SPOT LTC",
    "Spot Spot",
  ]);

  const selectedContractType = contractType?.[0] ?? "";
  const isSpotSelected = spotContractTypes.has(selectedContractType);

  // Only send negotiation_date for spot contract type.
  // negotiationDateForSpot is expected to be a YYYY-MM-DD string.
  // TODO: map the existing week-of filters to a concrete date.
  const negotiationDateForSpot = isSpotSelected
    ? fetchFilters?.date?.[0]?.month_year ?? null
    : null;


  console.log("[VendorQuoteAssessment] quoteDetails payload", {
    contractType,
    isSpotSelected,
    weekOf: fetchFilters?.date?.[0],
    filters,
  });

  const { quoteDetails, isLoading: quotesLoading } = useQuoteDetailsData(
    filters.commodity,
    filters.districts && filters.districts.length > 0
      ? filters.districts
      : fallbackDistricts,
    filters.commodity === "Copper Wire" ? vendor : filters.vendor,
    contractType,
    null,
    benchmark ? [benchmark] : null,
    navItem,
    isSpotSelected ? negotiationDateForSpot : null,
  );


  const { res: skuData, isLoading: skuRatioLoading, refetch: refetchSkuData } = useSkuRatioData(
    filters.commodity,
    skuFilters.districts.length > 0 ? skuFilters.districts : fallbackDistricts,
    skuFilters.vendor.length > 0 ? skuFilters.vendor : fallbackVendors,
    quotes && filters.commodity !== "Copper Wire" ? [quotes] : null,
    skuToggle,
    quoteToggle,
    monthYear,
    navItem === "sKURatioAnalysis"
      ? expandContractTypes(skuFilters.contractTypes)
      : filters.contractTypes,
    null,
    benchmark ? [benchmark] : null,
    navItem,
  );

  const { res: skuQuoteDetails, isLoading: skuQuoteDetailsLoading } =
    useSkuQuoteDetailsData(
      filters.commodity,
      skuQuoteDetailsFilters.districts.length > 0
        ? skuQuoteDetailsFilters.districts
        : fallbackDistricts,
      skuQuoteDetailsFilters.vendor.length > 0
        ? skuQuoteDetailsFilters.vendor
        : fallbackVendors,
      quotes && filters.commodity !== "Copper Wire" ? [quotes] : null,
      skuQuoteDetailsToggle,
      skuQuoteDetailsFilters.date.length > 0
        ? skuQuoteDetailsFilters.date
        : fallbackMonths,
      navItem === "quoteSummary"
        ? expandContractTypes(skuQuoteDetailsFilters.contractTypes)
        : filters.contractTypes,
      null,
      navItem === "quoteSummary"
        ? skuQuoteDetailsFilters.benchmark
          ? [skuQuoteDetailsFilters.benchmark]
          : null
        : benchmark
          ? [benchmark]
          : null,
      navItem,
      isQuoteSummaryReady &&
        (filters.commodity === "Copper Wire"
          ? skuQuoteDetailsFilters.contractTypes.length > 0
          : true),
    );

  useEffect(() => {
    setSkuFilters((prev) => ({
      ...prev,
      commodity: filters.commodity,
      contractTypes: contractType ?? [],
      benchmark,
    }));
  }, [filters.commodity, contractType, benchmark]);

  useEffect(() => {
    setSkuQuoteDetailsFilter((prev) => ({
      ...prev,
      commodity: filters.commodity,
    }));
  }, [filters.commodity]);

  useEffect(() => {
    if (isLoading || isFiltersError || !fetchFilters?.commodity?.length) {
      return;
    }

    const availableCommodities =
      fetchFilters.commodity?.map((item: any) => item.commodity) ?? [];
    const defaultCommodity =
      getDefaultCommodity(fetchFilters.commodity) ?? null;
    // Keep the current selection only when it is a valid option; otherwise fall
    // back to the default. This corrects the seeded Copper Wire value on any
    // deployment whose commodity list does not include it.
    const nextCommodity =
      filters.commodity && availableCommodities.includes(filters.commodity)
        ? filters.commodity
        : defaultCommodity;

    setFilters((prev) => ({
      ...prev,
      commodity: nextCommodity,
    }));

    const sortedQuotes = [...(fetchFilters.quotation_no ?? [])].sort(
      (a: any, b: any) => Number(a.quotation_no) - Number(b.quotation_no),
    );

    const latestQuote = sortedQuotes[sortedQuotes.length - 1]?.quotation_no;
    if (latestQuote) {
      setQuotes((prev: any) => {
        const normalizedPrev = String(prev);
        const isCurrentValid = sortedQuotes.some(
          (item: any) => String(item.quotation_no) === normalizedPrev,
        );
        return isCurrentValid ? normalizedPrev : String(latestQuote);
      });
    }

    const availableMonths = getMonthValues(fetchFilters);
    const defaultMonth = fetchFilters.month_year?.[0]?.month ?? "";
    if (navItem !== "sKURatioAnalysis") {
      setMonthYear((prev) =>
        prev && availableMonths.includes(prev) ? prev : defaultMonth,
      );
    }

    if (nextCommodity === "Copper Wire" && fetchFilters.benchmark?.length > 0) {
      const firstBenchmark = fetchFilters.benchmark[0]?.benchmark ?? "";

      const hasValidBenchmark = fetchFilters.benchmark.some(
        (item) => item.benchmark === benchmark,
      );

      const hasValidQuoteSummaryBenchmark = fetchFilters.benchmark.some(
        (item) => item.benchmark === skuQuoteDetailsFilters.benchmark,
      );

      if (!benchmark || !hasValidBenchmark) {
        setBenchmark(firstBenchmark);
      }

      if (
        !skuQuoteDetailsFilters.benchmark ||
        !hasValidQuoteSummaryBenchmark
      ) {
        setQuoteSummaryBenchmark(firstBenchmark);
        setSkuQuoteDetailsFilter((prev) => ({
          ...prev,
          benchmark: firstBenchmark,
        }));
      }
    } else {
      if (benchmark) {
        setBenchmark("");
      }

      if (skuQuoteDetailsFilters.benchmark) {
        setQuoteSummaryBenchmark("");
        setSkuQuoteDetailsFilter((prev) => ({
          ...prev,
          benchmark: "",
        }));
      }
    }
  }, [
    isLoading,
    isFiltersError,
    fetchFilters,
    filters.commodity,
    benchmark,
    skuQuoteDetailsFilters.benchmark,
    navItem,
  ]);

  useEffect(() => {
    if (isFiltersError || !Object.keys(fetchFilters).length) {
      setAllFilters((prev) => ({
        ...INITIAL_ALL_FILTERS,
        commodity: prev.commodity,
      }));

      if (changedFilter.current === "Commodity") {
        setDistrict([]);
        setVendor([]);
        setSkuDistrict([]);
        setSkuVendor([]);
        setSkuQuoteDetailsDistrict([]);
        setSkuQuoteDetailsVendor([]);
        setSkuQuoteDetailsDate([]);
      }


      changedFilter.current = null;
      return;
    }

    const nextFilters = removeDateField(fetchFilters);

    if (!changedFilter.current) {
      setAllFilters(nextFilters);
      return;
    }

    if (changedFilter.current === "Commodity") {
      setAllFilters((prev) => ({
        ...nextFilters,
        commodity: prev.commodity,
      }));
      setDistrict([]);
      setVendor([]);
      setMonthYear("");
    } else if (changedFilter.current === "district") {
      setAllFilters((prev) => ({
        ...nextFilters,
        vendor: nextFilters.vendor,
      }));
    } else {
      setAllFilters(nextFilters);
    }

    changedFilter.current = null;
  }, [fetchFilters, isFiltersError]);

  useEffect(() => {
    if (navItem !== "districtComparison" || isLoading || isFiltersError) {
      return;
    }

    const availableMonths = getMonthValues(fetchFilters);
    const defaultMonth = fetchFilters.month_year?.[0]?.month ?? "";
    const hasValidMonth =
      monthYear.length > 0 && availableMonths.includes(monthYear);

    if (!hasValidMonth && defaultMonth) {
      setMonthYear(defaultMonth);
    }

    if (filters.commodity === "Copper Wire") {
      const availableQuoteTypes =
        fetchFilters.contract_type?.map((item) => item.contract_type) ?? [];
      if (availableQuoteTypes.length > 0) {
        const currentType = contractType?.[0] ?? "";
        const isCurrentValid = availableQuoteTypes.includes(currentType);
        const nextType = isCurrentValid
          ? currentType
          : availableQuoteTypes.includes("Firm LTC")
            ? "Firm LTC"
            : availableQuoteTypes[0] ?? "";

        if (nextType !== currentType) {
          setContractType(nextType ? [nextType] : []);
          setFilters((prev) => ({
            ...prev,
            contractTypes: nextType ? [nextType] : [],
          }));
        }
      }
    } else if ((contractType?.length ?? 0) > 0) {
      setContractType([]);
      setFilters((prev) => ({
        ...prev,
        contractTypes: [],
      }));
    }
  }, [
    navItem,
    isLoading,
    isFiltersError,
    fetchFilters,
    monthYear,
    filters.commodity,
    contractType,
  ]);

  useEffect(() => {
    if (navItem !== "sKURatioAnalysis" || isLoading || isFiltersError) {
      return;
    }

    // Ratio Analysis month filter always sources its options from the API's
    // `month_year` key, so validate/default against that list.
    const availableMonths = getMonthValues(fetchFilters);
    const defaultMonthValue = firstMonth;
    const hasValidMonth =
      monthYear.length > 0 && availableMonths.includes(monthYear);

    if (!hasValidMonth && defaultMonthValue) {
      setMonthYear(defaultMonthValue);
    }

    if (!skuToggle) {
      if (skuDistrict.length === 0 && firstDistrict) {
        setSkuDistrict([firstDistrict]);
        setSkuFilters((prev) => ({
          ...prev,
          districts: [firstDistrict],
        }));
      }
      return;
    }

    if (skuVendor.length === 0 && firstVendor) {
      setSkuVendor([firstVendor]);
      setSkuFilters((prev) => ({
        ...prev,
        vendor: [firstVendor],
      }));
    }
  }, [
    navItem,
    isLoading,
    isFiltersError,
    skuToggle,
    firstDistrict,
    firstVendor,
    firstMonthOf,
    isSkuMonthOf,
    firstMonth,
    monthYear,
    skuDistrict.length,
    skuVendor.length,
  ]);

  const previousQuoteSummaryMode = useRef<QuoteSummaryMode>(skuQuoteDetailsToggle);

  // Auto-default contract type to "Firm/Float" for all tabs when Copper Wire is selected
  useEffect(() => {
    if (filters.commodity !== "Copper Wire" || isLoading || isFiltersError) {
      return;
    }

    if (
      navItem === "quoteDetails" &&
      (!contractType || contractType.length === 0)
    ) {
      const nextValue = ["Firm/Float"];
      setContractType(nextValue);
      setFilters((prev) => ({ ...prev, contractTypes: nextValue }));
    }

    if (
      navItem === "districtComparison" &&
      (!contractType || contractType.length === 0)
    ) {
      const nextValue = ["Firm LTC"];
      setContractType(nextValue);
      setFilters((prev) => ({ ...prev, contractTypes: nextValue }));
    }

    if (navItem === "sKURatioAnalysis" && (!contractType || contractType.length === 0)) {
      const nextValue = ["Firm/Float"];
      setContractType(nextValue);
      setSkuFilters((prev) => ({ ...prev, contractTypes: nextValue }));
    }

    if (navItem === "quoteSummary" && (!quoteSummaryContractType || quoteSummaryContractType.length === 0)) {
      const nextValue = ["Firm/Float"];
      setQuoteSummaryContractType(nextValue);
      setSkuQuoteDetailsFilter((prev) => ({ ...prev, contractTypes: nextValue }));
    }
  }, [navItem, filters.commodity, isLoading, isFiltersError]);

  useEffect(() => {
    if (navItem !== "quoteSummary" || isLoading || isFiltersError) {
      return;
    }

    if (!isQuoteSummaryReady) {
      setIsQuoteSummaryReady(true);
    }

    const modeChanged =
      previousQuoteSummaryMode.current !== skuQuoteDetailsToggle;
    previousQuoteSummaryMode.current = skuQuoteDetailsToggle;

    const pickSingleValue = (values: string[], fallback: string): string[] => {
      if (values.length > 0) {
        return [values[0]];
      }
      return fallback ? [fallback] : [];
    };

    const defaultQuoteSummaryDate = firstMonth;


    const availableQuoteSummaryMonths = getMonthValues(fetchFilters);
    const resolveQuoteSummaryDate = (): string[] => {
      const [current] = skuQuoteDetailsDate;
      const hasValidCurrent =
        Boolean(current) &&
        (availableQuoteSummaryMonths.length === 0 ||
          availableQuoteSummaryMonths.includes(current));
      if (hasValidCurrent) {
        return [current];
      }
      return defaultQuoteSummaryDate ? [defaultQuoteSummaryDate] : [];
    };

    if (skuQuoteDetailsToggle === "supplier") {
      const nextVendor = modeChanged ? [] : skuQuoteDetailsVendor;
      const nextDistrict = pickSingleValue(
        skuQuoteDetailsDistrict,
        firstDistrict,
      );
      const nextDate =
        isQuoteSummaryMonthOf && defaultQuoteSummaryDate
          ? resolveQuoteSummaryDate()
          : pickSingleValue(skuQuoteDetailsDate, defaultQuoteSummaryDate);

      if (!areSameSelections(skuQuoteDetailsVendor, nextVendor)) {
        setSkuQuoteDetailsVendor(nextVendor);
      }

      if (!areSameSelections(skuQuoteDetailsDistrict, nextDistrict)) {
        setSkuQuoteDetailsDistrict(nextDistrict);
      }

      if (!areSameSelections(skuQuoteDetailsDate, nextDate)) {
        setSkuQuoteDetailsDate(nextDate);
      }

      setSkuQuoteDetailsFilter((prev) => ({
        ...prev,
        vendor: nextVendor,
        districts: nextDistrict,
        date: nextDate,
      }));

      return;
    }

    if (skuQuoteDetailsToggle === "district") {
      const nextDistrict = modeChanged ? [] : skuQuoteDetailsDistrict;
      const nextVendor = pickSingleValue(skuQuoteDetailsVendor, firstVendor);
      const nextDate =
        isQuoteSummaryMonthOf && defaultQuoteSummaryDate
          ? resolveQuoteSummaryDate()
          : pickSingleValue(skuQuoteDetailsDate, defaultQuoteSummaryDate);

      if (!areSameSelections(skuQuoteDetailsDistrict, nextDistrict)) {
        setSkuQuoteDetailsDistrict(nextDistrict);
      }

      if (!areSameSelections(skuQuoteDetailsVendor, nextVendor)) {
        setSkuQuoteDetailsVendor(nextVendor);
      }

      if (!areSameSelections(skuQuoteDetailsDate, nextDate)) {
        setSkuQuoteDetailsDate(nextDate);
      }

      setSkuQuoteDetailsFilter((prev) => ({
        ...prev,
        districts: nextDistrict,
        vendor: nextVendor,
        date: nextDate,
      }));

      return;
    }

    const nextDate =
      isQuoteSummaryMonthOf && defaultQuoteSummaryDate
        ? resolveQuoteSummaryDate()
        : modeChanged
          ? []
          : skuQuoteDetailsDate;
    const nextDistrict = pickSingleValue(skuQuoteDetailsDistrict, firstDistrict);
    const nextVendor = pickSingleValue(skuQuoteDetailsVendor, firstVendor);

    if (!areSameSelections(skuQuoteDetailsDate, nextDate)) {
      setSkuQuoteDetailsDate(nextDate);
    }

    if (!areSameSelections(skuQuoteDetailsDistrict, nextDistrict)) {
      setSkuQuoteDetailsDistrict(nextDistrict);
    }

    if (!areSameSelections(skuQuoteDetailsVendor, nextVendor)) {
      setSkuQuoteDetailsVendor(nextVendor);
    }

    setSkuQuoteDetailsFilter((prev) => ({
      ...prev,
      date: nextDate,
      districts: nextDistrict,
      vendor: nextVendor,
    }));
  }, [
    navItem,
    isLoading,
    isFiltersError,
    skuQuoteDetailsToggle,
    firstDistrict,
    firstVendor,
    firstMonthOf,
    firstMonth,
    isQuoteSummaryMonthOf,
    filters.commodity,
    skuQuoteDetailsDate,
    skuQuoteDetailsDistrict,
    skuQuoteDetailsVendor,
    isQuoteSummaryReady,
  ]);

  const updateFilters = (patch: Partial<NegotiationSelectedFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const updateSkuFilters = (patch: Partial<SkuFiltersState>) => {
    setSkuFilters((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const updateQuoteSummaryFilters = (
    patch: Partial<QuoteSummaryFiltersState>,
  ) => {
    setSkuQuoteDetailsFilter((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const snapshotSelection = (value: string[] | null | undefined) => {
    currentSelection.current = value ? [...value] : null;
  };

  const handleTabClick = (tabName: NavItem, tabLabel: string) => {
    setNavItem(tabName);
    trackTabClick(tabLabel, "Vendor Quote Assessment");
  };

  const handleCommodityChange = (value: string | null) => {
    changedFilter.current = "Commodity";

    updateFilters({
      commodity: value && value.length > 0 ? value : null,
      districts: null,
      vendor: null,
    });

    setSkuQuoteDetailsToggle("supplier");

    setDistrict(null);
    setVendor(null);
    setMonthYear("");
    setContractType([]);
    setBenchmark("");

    if (skuToggle) {
      const nextVendor = firstVendor ? [firstVendor] : [];
      setSkuVendor(nextVendor);
      setSkuDistrict([]);
      updateSkuFilters({
        vendor: nextVendor,
        districts: [],
        contractTypes: [],
        benchmark: "",
      });
    } else {
      const nextDistrict = firstDistrict ? [firstDistrict] : [];
      setSkuDistrict(nextDistrict);
      setSkuVendor([]);
      updateSkuFilters({
        vendor: [],
        districts: nextDistrict,
        contractTypes: [],
        benchmark: "",
      });
    }

    setSkuQuoteDetailsVendor([]);
    setSkuQuoteDetailsDistrict([]);
    setSkuQuoteDetailsDate([]);
    setQuoteSummaryContractType([]);
    setQuoteSummaryBenchmark("");

    updateQuoteSummaryFilters({
      vendor: [],
      districts: [],
      date: [],
      contractTypes: [],
      benchmark: "",
    });
  };

  const showBaseDistrictAndVendorFilters =
    navItem !== "sKURatioAnalysis" &&
    navItem !== "quoteSummary" &&
    navItem !== "districtComparison";

  const showSkuCopperWireContractFilters =
    filters.commodity === "Copper Wire" &&
    navItem !== "districtComparison" &&
    navItem !== "quoteSummary";

  const showQuoteSummaryCopperWireContractFilters =
    filters.commodity === "Copper Wire" && navItem === "quoteSummary";

  const isScreenLoading =
    isLoading ||
    renegotiateDataLoading ||
    quotesLoading ||
    skuRatioLoading ||
    skuQuoteDetailsLoading ||
    districtComparisionLoading;

  const activeContractTypes =
    navItem === "quoteSummary" || navItem === "quoteDetails"
      ? (quoteSummaryContractType?.length
          ? quoteSummaryContractType
          : contractType) ?? []
      : contractType ?? [];

  const isActiveMonthOf =
    filters.commodity === "Copper Wire" &&
    activeContractTypes.some(
      (value) => value === "Firm" || value === "Float" || value === "Firm/Float",
    );

  const negotiationDateLabel =
    Object.keys(fetchFilters).length > 0
      ? (filters.commodity !== "Copper Wire" || isActiveMonthOf)
        ? convertDateToMonthYear(fetchFilters?.date?.[0]?.month_year)
        : formatISOToLong(fetchFilters?.date?.[0]?.month_year)
      : "";

  const handleBaseDistrictClose = () => {
    changedFilter.current = "district";
    if (!areSameSelections(currentSelection.current, district)) {
      updateFilters({ districts: district });
    }
  };

  const handleBaseDistrictClean = () => {
    changedFilter.current = "district";
    setDistrict(null);
    updateFilters({ districts: null });
  };

  const handleBaseVendorClose = () => {
    if (!areSameSelections(currentSelection.current, vendor)) {
      changedFilter.current = "vendor";
      updateFilters({ vendor });
    }
  };

  const handleBaseVendorClean = () => {
    changedFilter.current = "vendor";
    setVendor(null);
    updateFilters({ vendor: null });
  };

  const handleDistrictComparisonExcelDownload = () => {
    void downloadDistrictComparisonExcel({
      districtComparisionData,
      commodity: filters.commodity,
      monthYear,
      contractType,
      benchmark,
      quotes,
    });
  };

   const fetchSkuQuoteDetailsForDownload = async () => {
    const districtParam =
      skuQuoteDetailsFilters.districts.length > 0
        ? skuQuoteDetailsFilters.districts
        : fallbackDistricts;
    const vendorParam =
      skuQuoteDetailsFilters.vendor.length > 0
        ? skuQuoteDetailsFilters.vendor
        : fallbackVendors;
    const monthYearParam =
      skuQuoteDetailsFilters.date.length > 0
        ? skuQuoteDetailsFilters.date
        : fallbackMonths;
    const res = await getSkuQuoteDetailsData(
      filters.commodity,
      districtParam,
      vendorParam,
      quotes && filters.commodity !== "Copper Wire" ? [quotes] : null,
      skuQuoteDetailsToggle === 'supplier' ? 'vendor' : skuQuoteDetailsToggle, // match live query's mapping,
      monthYearParam,
      expandContractTypes(skuQuoteDetailsFilters.contractTypes),
      null,
     ["All"],// benchmark forced to null -> "All", download-only
      navItem,
    );
    return res?.data?.data ?? {};
  };

  const skuRatioDownloadRef = useRef<(() => void) | null>(null);

  const handleSkuApproveClick = () => {
    // Trigger the SKU Ratio Analysis Excel export registered by the child
    skuRatioDownloadRef.current?.();
  };

  return (
    <>
      {isScreenLoading ? (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6 }}
        />
      ) : (
        <div
          className={`flex flex-col justify-stretch items-stretch h-full ${navItem !== "sKURatioAnalysis" ? "quote-assessment" : ""
            }`}
        >
          <VendorQuoteAssessmentHeader
            width={120}
            navItem={navItem}
            allFilters={allFilters}
            filters={filters}
            district={district}
            vendor={vendor}
            quotes={quotes}
            monthYear={monthYear}
            contractType={contractType}
            benchmark={benchmark}
            firstMonth={firstMonth}
            monthOfFilters={monthOfFilters}
            skuToggle={skuToggle}
            skuQuoteDetailsToggle={skuQuoteDetailsToggle}
            skuDistrict={skuDistrict}
            skuVendor={skuVendor}
            skuQuoteDetailsDistrict={skuQuoteDetailsDistrict}
            skuQuoteDetailsVendor={skuQuoteDetailsVendor}
            skuQuoteDetailsDate={skuQuoteDetailsDate}
            quoteSummaryContractType={quoteSummaryContractType}
            quoteSummaryBenchmark={quoteSummaryBenchmark}
            negotiationDateLabel={negotiationDateLabel}
            showBaseDistrictAndVendorFilters={showBaseDistrictAndVendorFilters}
            showSkuCopperWireContractFilters={showSkuCopperWireContractFilters}
            showQuoteSummaryCopperWireContractFilters={showQuoteSummaryCopperWireContractFilters}
            onTabClick={handleTabClick}
            onCommodityChange={handleCommodityChange}
            onBaseDistrictOpen={() => snapshotSelection(district)}
            onBaseDistrictChange={(value) => {
              const nextValue = value.length > 0 ? value : null;
              setDistrict(nextValue);
            }}
            onBaseDistrictClose={handleBaseDistrictClose}
            onBaseDistrictClean={handleBaseDistrictClean}
            onBaseVendorOpen={() => snapshotSelection(vendor)}
            onBaseVendorChange={(value) => {
              const nextValue = value.length > 0 ? value : null;
              setVendor(nextValue);
            }}
            onBaseVendorClose={handleBaseVendorClose}
            onBaseVendorClean={handleBaseVendorClean}
            onQuoteSummaryDistrictChange={(value) => {
              const nextValue = value.length > 0 ? value : [];
              setSkuQuoteDetailsDistrict(nextValue);
              if (skuQuoteDetailsToggle !== "district") {
                updateQuoteSummaryFilters({ districts: nextValue });
              }
            }}
            onQuoteSummaryDistrictClose={() => {
              updateQuoteSummaryFilters({ districts: skuQuoteDetailsDistrict });
            }}
            onQuoteSummaryDistrictClean={() => {
              setSkuQuoteDetailsDistrict([]);
              updateQuoteSummaryFilters({ districts: [] });
            }}
            onQuoteSummarySupplierChange={(value) => {
              const nextValue = value.length > 0 ? value : [];
              setSkuQuoteDetailsVendor(nextValue);
              if (skuQuoteDetailsToggle !== "supplier") {
                updateQuoteSummaryFilters({ vendor: nextValue });
              }
            }}
            onQuoteSummarySupplierClose={() => {
              updateQuoteSummaryFilters({ vendor: skuQuoteDetailsVendor });
            }}
            onQuoteSummarySupplierClean={() => {
              setSkuQuoteDetailsVendor([]);
              updateQuoteSummaryFilters({ vendor: [] });
            }}
            onQuoteSummaryQuoteChange={(value) => {
              if (value) {
                setQuotes(value);
              }
            }}
            onQuoteSummaryDateChange={(value) => {
              const nextValue = value.length > 0 ? value : [];
              setSkuQuoteDetailsDate(nextValue);
              // Always sync the query-backing filter date so the table below
              // updates immediately for every mode (supplier/district/month).
              // Previously "month" mode deferred this to the defaults effect
              // (which early-returns while filters are loading) or onClose,
              // causing the table to update only sometimes.
              updateQuoteSummaryFilters({ date: nextValue });
            }}
            onQuoteSummaryDateClose={() => {
              updateQuoteSummaryFilters({ date: skuQuoteDetailsDate });
            }}
            onQuoteSummaryDateClean={() => {
              setSkuQuoteDetailsDate([]);
              updateQuoteSummaryFilters({ date: [] });
            }}
            onSkuDistrictChange={(value) => {
              const nextValue = value.length > 0 ? value : [];
              setSkuDistrict(nextValue);
              if (!skuToggle) {
                updateSkuFilters({ districts: nextValue });
              }
            }}
            onSkuDistrictClose={() => {
              updateSkuFilters({ districts: skuDistrict });
            }}
            onSkuDistrictClean={() => {
              setSkuDistrict([]);
              updateSkuFilters({ districts: [] });
            }}
            onSkuVendorChange={(value) => {
              const nextValue = value.length > 0 ? value : [];
              setSkuVendor(nextValue);
              if (skuToggle) {
                updateSkuFilters({ vendor: nextValue });
              }
            }}
            onSkuVendorClose={() => {
              updateSkuFilters({ vendor: skuVendor });
            }}
            onSkuVendorClean={() => {
              setSkuVendor([]);
              updateSkuFilters({ vendor: [] });
            }}
            onMonthYearChange={(value) => {
              if (value) {
                setMonthYear(value);
              }
            }}
            onDistrictComparisonQuoteTypeChange={(value) => {
              const nextValue = value ? [value] : [];
              setContractType(nextValue);
              updateFilters({ contractTypes: nextValue });
            }}
            onDistrictComparisonQuoteTypeClean={() => {
              setContractType([]);
              updateFilters({ contractTypes: [] });
            }}
            onSharedQuoteChange={(value) => {
              if (value) {
                setQuotes(value);
              }
            }}
            onBenchmarkChange={(value) => {
              setBenchmark(value || "");
            }}
            onBenchmarkClean={() => {
              setBenchmark("");
            }}
            onContractTypeChange={(value) => {
              const nextValue = value ? [value] : [];
              setContractType(nextValue);
              if (navItem === "sKURatioAnalysis") {
                setMonthYear("");
                updateSkuFilters({ contractTypes: nextValue });
              } else {
                updateFilters({ contractTypes: nextValue });
              }
            }}
            onContractTypeClose={() => {}}
            onContractTypeClean={() => {
              const nextValue: string[] = [];
              setContractType(nextValue);
              if (navItem === "sKURatioAnalysis") {
                setMonthYear("");
                updateSkuFilters({ contractTypes: nextValue });
              } else {
                updateFilters({ contractTypes: nextValue });
              }
            }}
            onQuoteSummaryContractTypeChange={(value) => {
              const nextValue = value ? [value] : [];
              setQuoteSummaryContractType(nextValue);
              setSkuQuoteDetailsDate([]);
              updateQuoteSummaryFilters({ contractTypes: nextValue, date: [] });
            }}
            onQuoteSummaryContractTypeClose={() => {}}
            onQuoteSummaryContractTypeClean={() => {
              setQuoteSummaryContractType([]);
              setSkuQuoteDetailsDate([]);
              updateQuoteSummaryFilters({ contractTypes: [], date: [] });
            }}
            onQuoteSummaryBenchmarkChange={(value) => {
              const nextValue = value || "";
              setQuoteSummaryBenchmark(nextValue);
              updateQuoteSummaryFilters({ benchmark: nextValue });
            }}
            onQuoteSummaryBenchmarkClean={() => {
              setQuoteSummaryBenchmark("");
              updateQuoteSummaryFilters({ benchmark: "" });
            }}
            onOpenPurchasingConditions={() => setOpenDownloadModal(true)}
            onDownloadDistrictComparison={handleDistrictComparisonExcelDownload}
            onApproveSkuClick={handleSkuApproveClick}
            userDetails={userDetailsRedux}
          />

          <VendorQuoteAssessmentContent
            navItem={navItem}
            filters={filters}
            isQuoteDetailsMonthOf={isSkuMonthOf}
            benchmark={benchmark}
            monthYear={monthYear}
            quotes={quotes}
            fetchFilters={fetchFilters}
            vendorData={vendorData}
            quoteDetails={quoteDetails}
            districtComparisionData={districtComparisionData}
            skuQuoteDetailsFilters={skuQuoteDetailsFilters}
            skuQuoteDetailsToggle={skuQuoteDetailsToggle}
            setSkuQuoteDetailsToggle={setSkuQuoteDetailsToggle}
            skuQuoteDetails={skuQuoteDetails}
            skuQuoteDetailsDistrict={skuQuoteDetailsDistrict}
            skuQuoteDetailsVendor={skuQuoteDetailsVendor}
            skuQuoteDetailsDate={skuQuoteDetailsDate}
            skuFilters={skuFilters}
            skuToggle={skuToggle}
            setSkuToggle={setSkuToggle}
            setQuoteToggle={setQuoteToggle}
            quoteToggle={quoteToggle}
            skuData={skuData}
            userDetails={userDetailsRedux}
            skuRatioDownloadRef={skuRatioDownloadRef}
             fetchSkuQuoteDetailsForDownload={fetchSkuQuoteDetailsForDownload}
          />

          {openDownloadModal && allFilters.vendor.length > 0 && (
            <PurchasingConditionDownloadModal
              openDownloadModal={openDownloadModal}
              setOpenDownloadModal={setOpenDownloadModal}
              negotiationMonth={
                Object.keys(fetchFilters).length > 0
                  ? convertDateToMonthYear(fetchFilters?.date?.[0]?.month_year)
                  : ""
              }
              commodity={filters.commodity}
              vendorList={allFilters.vendor}
              baseSku={benchmark}
            />
          )}
        </div>
      )}
    </>
  );
};

export default VendorQuoteAssessment;